import React from "react";
//#region types
import { WintSuperkollAPIModelQualifiedShareDividend } from "wint-api-client/dist/types";
import { CurrencyCode } from "models/enums/currency";
import { DividendSpaceListProps } from "models/propTypes/annualAccountPropTypes";
import { TranslationKeys } from "translations";
import { SimpleTableRow } from "models/propTypes/simpleTableProps";
import { StyleEnums } from "models/enums";
//#endregion
//#region components
import { SimpleAccordionTable, TitleSmall } from "components/common";
import { useQualifiedShareDividendsService } from "serviceHooks";
import { Grid } from "@mui/material";
import Amount from "./amount";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const DividendSpaceList: React.FC<DividendSpaceListProps> = ({ dividend, financialYearId }) => {
  const qualifiedSharedDividendService = useQualifiedShareDividendsService();

  const [loading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<WintSuperkollAPIModelQualifiedShareDividend.ShareholderDividendInfo[]>([]);

  React.useEffect(() => {
    setLoading(true);
    qualifiedSharedDividendService.getShareholdersDividendInfo(financialYearId).then((result) => {
      setResult(result);
      setLoading(false);
    });
  }, []);

  const roundDownAmount = (amount: number) => Math.floor(amount);

  const convertToCurrency = (amount: number, retainSign?: boolean) => {
    let displayAmount = roundDownAmount(amount);
    displayAmount = retainSign ? displayAmount : Math.abs(displayAmount);

    return LocalizationHelperInstance.getFormattedCurrency(displayAmount, CurrencyCode.SEK, 0, 0);
  };

  const listItemWhenK10ByWint = (item: WintSuperkollAPIModelQualifiedShareDividend.ShareholderDividendInfo) => {
    const dividendForShareholder = roundDownAmount(dividend * item.ownershipQuotient);
    const maxDividendTaxedAtLowerRate = roundDownAmount(item.dividendSpace ?? 0);
    const dividendTaxedAtLowerRate =
      dividendForShareholder <= maxDividendTaxedAtLowerRate ? dividendForShareholder : maxDividendTaxedAtLowerRate;
    const dividendTaxedAsSalary = dividendForShareholder - dividendTaxedAtLowerRate;

    const displayType = dividendTaxedAsSalary > 0 ? StyleEnums.DisplayType.Warning : StyleEnums.DisplayType.None;

    return {
      title: item.shareHolderName ?? "",
      value: <Amount displayType={displayType}>{convertToCurrency(dividendForShareholder)}</Amount>,
      subContent: [
        {
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.OwnershipShare),
          value: `${LocalizationHelperInstance.getFormattedNumber(item.ownershipQuotient * 100, 0, 2)}%`,
        },
        {
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.DividendSpace),
          value: convertToCurrency(maxDividendTaxedAtLowerRate),
        },
        {
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.DividendTaxedAsSalary),
          value: (
            <Amount displayType={displayType}>
              {convertToCurrency(dividendTaxedAsSalary >= 0 ? dividendTaxedAsSalary : 0)}
            </Amount>
          ),
        },
      ],
    };
  };

  const listItemWhenK10NotByWint = (item: WintSuperkollAPIModelQualifiedShareDividend.ShareholderDividendInfo) => {
    const displayType = StyleEnums.DisplayType.Warning;
    const dividendForShareholder = roundDownAmount(dividend * item.ownershipQuotient);

    return {
      title: item.shareHolderName ?? "",
      value: <Amount displayType={displayType}>{convertToCurrency(dividendForShareholder)}</Amount>,
      subContent: [
        {
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.OwnershipShare),
          value: `${LocalizationHelperInstance.getFormattedNumber(item.ownershipQuotient * 100, 0, 2)}%`,
        },
        {
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.DividendSpace),
          value: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.NotManagedByWint),
        },
        {
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.DividendTaxedAsSalary),
          value: (
            <Amount displayType={displayType}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.DividendUnknown)}
            </Amount>
          ),
        },
      ],
    };
  };

  const listItemWhenShareOwnerIsLegalEntity = (
    item: WintSuperkollAPIModelQualifiedShareDividend.ShareholderDividendInfo
  ) => {
    const dividendForShareholder = roundDownAmount(dividend * item.ownershipQuotient);

    return {
      title: item.shareHolderName ?? "",
      value: <Amount displayType={StyleEnums.DisplayType.None}>{convertToCurrency(dividendForShareholder)}</Amount>,
      subContent: [
        {
          title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.OwnershipShare),
          value: `${LocalizationHelperInstance.getFormattedNumber(item.ownershipQuotient * 100, 0, 2)}%`,
        },
      ],
    };
  };

  const dividendSpaceListItems = React.useMemo(() => {
    const dividendSpaceListItems: SimpleTableRow[] = result.map((item) => {
      if (item.isLegalEntity) {
        return listItemWhenShareOwnerIsLegalEntity(item);
      }
      if (item.k10HandledByWint) {
        return listItemWhenK10ByWint(item);
      }
      return listItemWhenK10NotByWint(item);
    });

    return dividendSpaceListItems;
  }, [dividend, result]);

  return result?.length && !loading ? (
    <Grid container rowGap={1} className={`${cssExports.mtb10} ${cssExports.dividendSpaceList}`}>
      <TitleSmall
        title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.AnnualAccount.DividendPerOwner)}
        className={cssExports.smaller}
      />
      <SimpleAccordionTable items={dividendSpaceListItems} />
    </Grid>
  ) : (
    <></>
  );
};

export default DividendSpaceList;
